import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { selectWarehouse, useCurrentWarehouseId } from '../../lib/warehouse';
import type { WarehouseListItem } from './types';

/**
 * S2 仓库选择页。三个"关键数字"（产品数/预警数/负库存数）依赖 M4/M5/M8 尚未
 * 建立的数据，先以占位符显示——见 M2 计划报告第 10 条记录的范围决定。
 */
export function WarehouseSelectPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation() as { state?: { from?: { pathname: string } } };
  const currentId = useCurrentWarehouseId();
  const [remember, setRemember] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['warehouses', 'mine'],
    queryFn: () => apiFetch<{ items: WarehouseListItem[] }>('/warehouses/mine'),
  });

  // 已经选过（比如从切换器主动跳回这里改选）且用户没有主动要求重选，
  // 不需要拦在这个页面上。
  if (currentId && !location.state?.from) {
    return <Navigate to="/dashboard" replace />;
  }

  const pick = (id: string): void => {
    selectWarehouse(id, remember);
    void queryClient.invalidateQueries({ queryKey: ['session', 'me'] });
    navigate(location.state?.from?.pathname ?? '/dashboard', { replace: true });
  };

  const items = data?.items ?? [];

  return (
    <div className="auth-page">
      <div className="warehouse-select">
        <h1 className="auth-page__brand">{t('warehouses.selectTitle')}</h1>

        {isPending && <p>{t('app.loading')}</p>}

        {!isPending && items.length === 0 && (
          <div className="empty-state">
            <p>{t('warehouses.noAccess')}</p>
          </div>
        )}

        {items.length > 0 && (
          <>
            <div className="warehouse-grid">
              {items.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  className="warehouse-card"
                  onClick={() => pick(w.id)}
                >
                  <span className="warehouse-card__name">{w.name}</span>
                  <span className="warehouse-card__code">{w.code}</span>
                  {w.roleCodes.length > 0 && (
                    <span className="warehouse-card__roles">
                      {w.roleCodes.map((r) => t(`role.${r.toLowerCase()}`, r)).join(' · ')}
                    </span>
                  )}
                  <span className="warehouse-card__stats">{t('warehouses.statsPending')}</span>
                </button>
              ))}
            </div>
            <label className="switch" style={{ marginTop: 'var(--space-4)' }}>
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span className="switch__track" />
              <span>{t('warehouses.setDefault')}</span>
            </label>
          </>
        )}
      </div>
    </div>
  );
}
